import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../services/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  CreditCardIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const UpgradeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [fullRequestDetails, setFullRequestDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [modalAction, setModalAction] = useState('');

  useEffect(() => {
    loadUpgradeRequests();
  }, []);

  const loadUpgradeRequests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/upgrade-requests');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to load upgrade requests:', error);
      toast.error('Failed to load upgrade requests');
    } finally {
      setLoading(false);
    }
  };

  // Load full request details including payment proof
  const viewRequestDetails = async (request) => {
    try {
      const response = await apiClient.get(`/admin/upgrade-requests/${request.id}`);
      setFullRequestDetails(response.data.request);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Failed to load request details:', error);
      toast.error('Failed to load request details');
    }
  };

  const downloadPaymentProof = async (request) => {
    if (!request.paymentProofUrl) {
      toast.error('No payment proof available');
      return;
    }

    try {
      // Download the file
      window.open(request.paymentProofUrl, '_blank');
    } catch (error) {
      console.error('Failed to download payment proof:', error);
      toast.error('Failed to download payment proof');
    }
  };

  const handleRequestAction = (request, action) => {
    setSelectedRequest(request);
    setModalAction(action);
    setAdminNotes('');
    setShowActionModal(true);
  };

  const processRequest = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(prev => ({ ...prev, [selectedRequest.id]: true }));
      
      await apiClient.post(`/admin/upgrade-requests/${selectedRequest.id}/${modalAction}`, {
        admin_notes: adminNotes
      });

      toast.success(`Request ${modalAction}d successfully`);
      setShowActionModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      setShowDetailsModal(false);
      setFullRequestDetails(null);
      loadUpgradeRequests(); // Refresh the list
    } catch (error) {
      console.error(`Failed to ${modalAction} request:`, error);
      toast.error(error.response?.data?.message || `Failed to ${modalAction} request`);
    } finally {
      setProcessing(prev => ({ ...prev, [selectedRequest.id]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', emoji: '⏳', label: 'Pending' },
      paid: { bg: 'bg-yellow-100', text: 'text-yellow-800', emoji: '💛', label: 'Paid - Awaiting Review' },
      active: { bg: 'bg-green-100', text: 'text-green-800', emoji: '✅', label: 'Active' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', emoji: '✅', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', emoji: '❌', label: 'Rejected' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', emoji: '⛔', label: 'Cancelled' }
    };
    return badges[status] || badges.pending;
  };

  const getPackageInfo = (packageCode) => {
    const packages = {
      basic: { name: 'Basic', color: 'bg-blue-100 text-blue-800' },
      starter: { name: 'Starter', color: 'bg-blue-100 text-blue-800' },
      premium: { name: 'Premium', color: 'bg-green-100 text-green-800' },
      enterprise: { name: 'Enterprise', color: 'bg-purple-100 text-purple-800' }
    };
    return packages[packageCode] || { name: packageCode, color: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const paidRequests = requests.filter(r => r.status === 'paid');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Subscription Requests</h2>
        <div className="text-sm text-gray-500">
          {paidRequests.length} awaiting review
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12">
          <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No subscription requests</h3>
          <p className="mt-1 text-sm text-gray-500">Users will appear here when they submit payment proof</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => {
                  const statusInfo = getStatusBadge(request.status);
                  const packageInfo = getPackageInfo(request.package);

                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {request.business_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{request.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${packageInfo.color}`}>
                          {packageInfo.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          PKR {request.total_price?.toLocaleString() || '0'}
                        </div>
                        <div className="text-xs text-gray-500">{request.months} month(s)</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                          {statusInfo.emoji} {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewRequestDetails(request)}
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center space-x-1 px-2 py-1 rounded hover:bg-blue-50"
                            title="View details and payment proof"
                          >
                            <EyeIcon className="h-4 w-4" />
                            <span className="text-xs">View</span>
                          </button>

                          {request.status === 'paid' && (
                            <>
                              <button
                                onClick={() => handleRequestAction(request, 'approve')}
                                disabled={processing[request.id]}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                              >
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRequestAction(request, 'reject')}
                                disabled={processing[request.id]}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                              >
                                <XCircleIcon className="h-3 w-3 mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && fullRequestDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Subscription Request Details
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setFullRequestDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Business Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Business Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Business Name</p>
                    <p className="text-sm font-medium text-gray-900">{fullRequestDetails.business_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Email</p>
                    <p className="text-sm font-medium text-gray-900">{fullRequestDetails.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">NTN/CNIC</p>
                    <p className="text-sm font-medium text-gray-900">{fullRequestDetails.user_ntn || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Mobile</p>
                    <p className="text-sm font-medium text-gray-900">{fullRequestDetails.mobile_number || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase">Address</p>
                    <p className="text-sm font-medium text-gray-900">{fullRequestDetails.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Subscription Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Subscription Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Package</p>
                    <p className="text-sm font-medium text-gray-900">{getPackageInfo(fullRequestDetails.package).name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Duration</p>
                    <p className="text-sm font-medium text-gray-900">{fullRequestDetails.months} month(s)</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Total Amount</p>
                    <p className="text-lg font-bold text-blue-600">PKR {fullRequestDetails.total_price?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Status</p>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(fullRequestDetails.status).bg} ${getStatusBadge(fullRequestDetails.status).text}`}>
                        {getStatusBadge(fullRequestDetails.status).emoji} {getStatusBadge(fullRequestDetails.status).label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Proof */}
              {fullRequestDetails.paymentProofUrl && (
                <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-yellow-600" />
                    Payment Proof
                  </h4>
                  
                  {/* Image Preview */}
                  <div className="mb-4 bg-white p-2 rounded border border-yellow-200">
                    <img
                      src={fullRequestDetails.paymentProofViewUrl}
                      alt="Payment Proof"
                      className="w-full h-auto max-h-80 object-contain rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>

                  {/* File Info & Download */}
                  <div className="flex items-center justify-between bg-white p-3 rounded border border-yellow-200">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{fullRequestDetails.payment_proof_url}</p>
                      <p className="text-xs text-gray-500 mt-1">Uploaded on {new Date(fullRequestDetails.created_at).toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => downloadPaymentProof(fullRequestDetails)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      Download
                    </button>
                  </div>
                </div>
              )}

              {/* Admin Notes Section */}
              {fullRequestDetails.status !== 'paid' && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Admin Notes</h4>
                  <p className="text-sm text-gray-600">{fullRequestDetails.admin_notes || 'No notes'}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3 border-t pt-6">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setFullRequestDetails(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>

              {fullRequestDetails.status === 'paid' && (
                <>
                  <button
                    onClick={() => {
                      setSelectedRequest(fullRequestDetails);
                      setModalAction('approve');
                      setAdminNotes('');
                      setShowActionModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Approve Request
                  </button>
                  <button
                    onClick={() => {
                      setSelectedRequest(fullRequestDetails);
                      setModalAction('reject');
                      setAdminNotes('');
                      setShowActionModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Reject Request
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Approve/Reject) */}
      {showActionModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalAction === 'approve' ? '✅ Approve' : '❌ Reject'} Subscription Request
                </h3>
                <button
                  onClick={() => setShowActionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-blue-50 rounded">
                <p className="text-sm text-gray-600">
                  <strong>{selectedRequest.business_name}</strong> has submitted payment proof for{' '}
                  <span className="font-medium">{getPackageInfo(selectedRequest.package).name}</span> plan
                  ({selectedRequest.months} month(s)) - <span className="font-bold text-blue-600">PKR {selectedRequest.total_price?.toLocaleString()}</span>
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 inline mr-1" />
                  Admin Notes {modalAction === 'reject' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={modalAction === 'approve' ? 'Optional notes for the user (e.g., "Payment verified, subscription activated")' : 'Please provide a reason for rejection...'}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowActionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={processRequest}
                  disabled={processing[selectedRequest.id] || (modalAction === 'reject' && !adminNotes.trim())}
                  className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                    modalAction === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                >
                  {processing[selectedRequest.id] ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {modalAction === 'approve' ? (
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 mr-2" />
                      )}
                      {modalAction === 'approve' ? 'Approve' : 'Reject'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeRequests;
